import { useState, useEffect, useRef, useMemo } from 'react';
import { WEB_API_BASE_URL } from '../config/config';

export interface DropdownOption {
  value: string;
  text: string;
}

export interface DependentDropdownConfig {
  fieldName: string;
  dataSource: string;
  dependencies?: string[];
  initialOptions?: DropdownOption[];
}

export interface UseDependentDropdownsReturn {
  dropdownOptions: Record<string, DropdownOption[]>;
  loadingFields: Set<string>;
  loadDropdownOptions: (fieldName: string) => Promise<void>;
  clearDependentFields: (changedField: string) => void;
}

export const useDependentDropdowns = (
  configs: DependentDropdownConfig[],
  formData: Record<string, any>,
  onChange: (fieldName: string, value: any) => void
): UseDependentDropdownsReturn => {
  const [dropdownOptions, setDropdownOptions] = useState<Record<string, DropdownOption[]>>({});
  const [loadingFields, setLoadingFields] = useState<Set<string>>(new Set());
  const lastResolvedUrls = useRef<Map<string, string>>(new Map());

  // Initialize dropdown options from configs
  useEffect(() => {
    configs.forEach(config => {
      if (config.initialOptions && !dropdownOptions[config.fieldName]) {
        setDropdownOptions(prev => ({
          ...prev,
          [config.fieldName]: config.initialOptions!
        }));
      }
    });
  }, [configs]);

  // Load dropdown options for a specific field
  const loadDropdownOptions = async (fieldName: string) => {
    const config = configs.find(c => c.fieldName === fieldName);
    if (!config) return;

    setLoadingFields(prev => new Set(prev).add(fieldName));

    try {
      let resolvedDataSource = config.dataSource;
      let shouldLoad = true;

      // Replace field placeholders with actual values from form data
      const placeholderRegex = /\{([^}]+)\}/g;
      const dependencies: string[] = [];
      let hasPlaceholders = false;

      // Check if dataSource has placeholders
      const matches = config.dataSource.match(placeholderRegex);
      if (matches) {
        hasPlaceholders = true;
        resolvedDataSource = config.dataSource.replace(placeholderRegex, (match: string, fieldName: string) => {
          dependencies.push(fieldName);
          const fieldValue = formData[fieldName] || '';
          return encodeURIComponent(fieldValue);
        });
      }

      // Determine if we should load
      if (hasPlaceholders) {
        // Only load if all dependencies have non-empty values
        shouldLoad = dependencies.every(dep => {
          const value = formData[dep];
          return value !== undefined && value !== null && value !== '';
        });

        if (!shouldLoad) {
          // Clear options if dependencies are not satisfied
          setDropdownOptions(prev => ({
            ...prev,
            [fieldName]: []
          }));
          return;
        }

        // Check if dependency values have changed since last load
        if (dropdownOptions[fieldName]) {
          const currentResolvedUrl = resolvedDataSource;
          const storedUrl = lastResolvedUrls.current.get(fieldName);
          if (storedUrl && storedUrl === currentResolvedUrl) {
            shouldLoad = false; // Don't reload if URL hasn't changed
          }
          lastResolvedUrls.current.set(fieldName, currentResolvedUrl);
        }
      } else {
        // For fields without dependencies, load once
        shouldLoad = !dropdownOptions[fieldName];
      }

      if (shouldLoad) {
        // Build the full URL
        let apiPath = resolvedDataSource;
        if (!apiPath.startsWith('/')) {
          apiPath = '/' + apiPath;
        }

        // Add authentication headers
        const authToken = localStorage.getItem('authToken');
        const jobPositionUID = localStorage.getItem('jobPositionUID') || '';
        const orgUID = localStorage.getItem('orgUID') || '';

        // Add JobPositionUID and OrgUID to the path if needed
        if (apiPath.includes('GetListDataByModule')) {
          const separator = apiPath.includes('?') ? '&' : '?';
          apiPath = `${apiPath}${separator}JobPositionUID=${jobPositionUID}&OrgUID=${orgUID}`;
        }

        // Call Web API
        const fullUrl = `${WEB_API_BASE_URL}${apiPath}`;

        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        };
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }


        const response = await fetch(fullUrl, {
          method: 'GET',
          headers
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        let options: DropdownOption[] = [];

        // Parse the response to extract options
        if (Array.isArray(data)) {
          options = data.map((item: any) => ({
            value: item.value || item.Value || item.code || item.Code || item.id || item.ID || item.uid || item.UID || '',
            text: item.text || item.Text || item.label || item.Label || item.name || item.Name || item.description || item.value || ''
          }));
        } else if (data.Data && data.Data.ModelData && Array.isArray(data.Data.ModelData)) {
          // Handle Web API response format: { Data: { ModelData: [...] } }
          options = data.Data.ModelData.map((item: any) => ({
            value: item.code || item.Code || item.value || item.Value || item.id || item.ID || item.uid || item.UID || '',
            text: item.name || item.Name || item.text || item.Text || item.label || item.Label || item.description || item.value || ''
          }));
        } else if (data.data && Array.isArray(data.data)) {
          options = data.data.map((item: any) => ({
            value: item.value || item.Value || item.code || item.Code || item.id || item.ID || item.uid || item.UID || '',
            text: item.text || item.Text || item.label || item.Label || item.name || item.Name || item.description || item.value || ''
          }));
        } else if (data.items && Array.isArray(data.items)) {
          options = data.items.map((item: any) => ({
            value: item.value || item.Value || item.code || item.Code || item.id || item.ID || item.uid || item.UID || '',
            text: item.text || item.Text || item.label || item.Label || item.name || item.Name || item.description || item.value || ''
          }));
        }

        setDropdownOptions(prev => ({
          ...prev,
          [fieldName]: options
        }));

      }
    } catch (error) {
      console.error(`Error loading dropdown options for ${fieldName}:`, error);
      setDropdownOptions(prev => ({
        ...prev,
        [fieldName]: []
      }));
    } finally {
      setLoadingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldName);
        return newSet;
      });
    }
  };

  // Track previous form data to detect actual changes
  const prevFormDataRef = useRef<Record<string, any>>({});

  // Load dropdown options when dependencies change
  useEffect(() => {
    configs.forEach(config => {
      if (config.dependencies && config.dependencies.length > 0) {
        // Check if all dependencies have values
        const allDependenciesSatisfied = config.dependencies.every(dep => {
          const value = formData[dep];
          return value !== undefined && value !== null && value !== '';
        });

        // Check if any dependency actually changed
        const dependencyChanged = config.dependencies.some(dep => {
          return prevFormDataRef.current[dep] !== formData[dep];
        });

        if (allDependenciesSatisfied && dependencyChanged) {
          loadDropdownOptions(config.fieldName);
        }
      } else {
        // Load non-dependent fields once
        if (!dropdownOptions[config.fieldName] || dropdownOptions[config.fieldName].length === 0) {
          loadDropdownOptions(config.fieldName);
        }
      }
    });

    // Update previous form data
    prevFormDataRef.current = { ...formData };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]); // Only depend on formData changes, ignore configs and dropdownOptions

  // Clear dependent fields when their dependencies change
  const clearDependentFields = (changedField: string) => {
    configs.forEach(config => {
      if (config.dependencies && config.dependencies.includes(changedField)) {
        // Clear this field's value and options
        onChange(config.fieldName, '');
        setDropdownOptions(prev => ({
          ...prev,
          [config.fieldName]: []
        }));
      }
    });
  };

  return {
    dropdownOptions,
    loadingFields,
    loadDropdownOptions,
    clearDependentFields
  };
};
